import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowUpDown, CheckSquare, Download, Eye, EyeOff, Home, Info, KeyRound, Lock, Pencil, RotateCcw, ShieldCheck, Trash2, UserCheck, UserX, Users as UsersIcon, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, downloadBlob, fmtDay, fmtDateIST, fmtINR } from "../../lib/api";
import { PaginationBar, TableRowsSkeleton, useDebounced } from "../../components/PaginationBar";
import { EditUserDialog, ResetPasswordDialog } from "./UsersPage";
import { UserAccessDialog } from "../../components/UserAccessDialog";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice } from "../../components/FeatureGate";

/**
 * Phase 215 — Citizens module (Municipal Admin). Citizen accounts are managed here, separately from Users & Staff.
 *  • Cascading Zone → Ward → Mohalla filters (via the citizen's linked properties), search by PIN / name / email / mobile,
 *    status, hide-inactive, sort, "Showing X of Y citizens".
 *  • Export: Excel workbook of the citizens currently filtered (Citizens / Demands / Payments sheets).
 *  • Super Admin switches (Tenant Config → Citizens): View / Edit / Delete — actions are hidden when a switch is off.
 *  • Delete is a soft delete → "Deleted Citizens" tab; restorable by the municipality until the end of that financial year.
 */

const SORTS_ACTIVE = [
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "name_desc", label: "Name (Z–A)" },
  { value: "recent", label: "Recently registered" },
  { value: "oldest", label: "Oldest first" },
];
const SORTS_DELETED = [
  { value: "deleted_recent", label: "Recently deleted" },
  { value: "restore_soon", label: "Restore deadline (soonest)" },
  { value: "name_asc", label: "Name (A–Z)" },
];

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";

function CitizenAvatar({ name, deleted = false }) {
  return (
    <span className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${deleted ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}
      aria-hidden="true">
      {initials(name)}
    </span>
  );
}

function PinCell({ citizen, testId }) {
  const pins = citizen.pins || [];
  if (pins.length === 0) {
    return <span className="text-xs text-muted-foreground italic" data-testid={`${testId}-none`}>Not linked</span>;
  }
  return (
    <span className="flex flex-col gap-0.5" data-testid={testId} title={pins.join(", ")}>
      <span className="font-mono text-xs font-medium">{pins[0]}</span>
      {pins.length > 1 && (
        <span className="text-[10px] text-muted-foreground">+{pins.length - 1} more propert{pins.length - 1 === 1 ? "y" : "ies"}</span>
      )}
    </span>
  );
}

const joinLabels = (arr) => (arr && arr.length ? arr.join(", ") : null);

export function RestoreWindowBadge({ citizen, testId }) {
  const open = !!citizen.restore_window_open;
  const d = citizen.days_left;
  if (!citizen.restore_until) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="flex flex-col gap-0.5" data-testid={testId}>
      <span className="text-sm tabular-nums">{fmtDay(citizen.restore_until)}</span>
      {open ? (
        <Badge variant="outline" className={`w-fit text-[10px] font-normal ${d != null && d <= 30 ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
          data-testid={`${testId}-open`}>
          {d === 0 ? "Last day today" : `${d} day${d === 1 ? "" : "s"} left`}{citizen.deleted_fy_label ? ` · FY ${citizen.deleted_fy_label}` : ""}
        </Badge>
      ) : (
        <Badge variant="outline" className="w-fit text-[10px] font-normal border-destructive/30 bg-destructive/5 text-destructive" data-testid={`${testId}-closed`}>
          <Lock className="h-2.5 w-2.5 mr-1" /> Window closed — Super Admin only
        </Badge>
      )}
    </span>
  );
}

export default function CitizensPage() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const featureOk = access.featureAllowed("users", "citizens");
  const ro = !access.canWrite("users", "citizens");

  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const dSearch = useDebounced(search, 350);
  const [zoneId, setZoneId] = useState("");
  const [wardId, setWardId] = useState("");
  const [mohallaId, setMohallaId] = useState("");
  const [status, setStatus] = useState("all");
  const [hideInactive, setHideInactive] = useState(() => localStorage.getItem(`hide_inactive_citizens_${code}`) === "1");
  const [sortActive, setSortActive] = useState(() => localStorage.getItem(`citizens_sort_${code}`) || "name_asc");
  const [sortDeleted, setSortDeleted] = useState("deleted_recent");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);
  const [reload, setReload] = useState(0);

  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [mohallas, setMohallas] = useState([]);

  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeText, setPurgeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Phase 216 — current-page selection + bulk actions
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null);      // "activate" | "deactivate" | "delete" | "restore"
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);      // last response with failures (shown in a dialog)

  useEffect(() => {
    api.get(`/t/${code}/masters/zones`).then((r) => setZones(r.data || [])).catch(() => {});
    api.get(`/t/${code}/masters/wards`).then((r) => setWards(r.data || [])).catch(() => {});
    api.get(`/t/${code}/masters/mohallas`).then((r) => setMohallas(r.data || [])).catch(() => {});
    api.get(`/t/${code}/citizens/access`).then((r) => setPerms(r.data)).catch(() => {});
  }, [code]);

  const zoneName = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z.zone_name])), [zones]);
  const wardsForZone = useMemo(() => (zoneId ? wards.filter((w) => w.zone_id === zoneId) : wards), [wards, zoneId]);
  const mohallasForWard = useMemo(() => {
    if (wardId) return mohallas.filter((m) => m.ward_id === wardId);
    if (zoneId) { const ids = new Set(wardsForZone.map((w) => w.id)); return mohallas.filter((m) => ids.has(m.ward_id)); }
    return mohallas;
  }, [mohallas, wardId, zoneId, wardsForZone]);

  const changeZone = (v) => { const z = v === "all" ? "" : v; setZoneId(z); setWardId(""); setMohallaId(""); setPage(1); };
  const changeWard = (v) => { const w = v === "all" ? "" : v; setWardId(w); setMohallaId(""); setPage(1); };
  const changeMohalla = (v) => { setMohallaId(v === "all" ? "" : v); setPage(1); };
  const changeStatus = (v) => { setStatus(v); setPage(1); };
  const setHide = (v) => { setHideInactive(v); setPage(1); localStorage.setItem(`hide_inactive_citizens_${code}`, v ? "1" : "0"); };
  const setSort = (v) => { if (tab === "deleted") setSortDeleted(v); else { setSortActive(v); localStorage.setItem(`citizens_sort_${code}`, v); } setPage(1); };
  const sort = tab === "deleted" ? sortDeleted : sortActive;
  const filtersActive = !!(zoneId || wardId || mohallaId || (status !== "all") || search.trim());
  const resetFilters = () => { setZoneId(""); setWardId(""); setMohallaId(""); setStatus("all"); setSearch(""); setPage(1); };
  const changeTab = (v) => { setTab(v); setPage(1); };

  const params = useMemo(() => {
    const p = { page, limit, sort };
    if (dSearch.trim()) p.search = dSearch.trim();
    if (zoneId) p.zone_id = zoneId;
    if (wardId) p.ward_id = wardId;
    if (mohallaId) p.mohalla_id = mohallaId;
    if (status !== "all") p.status = status;
    if (tab === "active" && hideInactive) p.hide_inactive = true;
    return p;
  }, [page, limit, sort, dSearch, zoneId, wardId, mohallaId, status, hideInactive, tab]);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/t/${code}/citizens${tab === "deleted" ? "/deleted" : ""}`, { params })
      .then((r) => { if (alive) { setData(r.data); if (r.data?.permissions) setPerms(r.data.permissions); } })
      .catch((e) => { if (alive) { setError(errMsg(e)); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, tab, params]);
  useEffect(() => load(), [load, reload]);
  const refresh = () => setReload((k) => k + 1);

  const canView = perms ? perms.can_view : true;
  const canEdit = !!perms?.can_edit && !ro;
  const canDelete = !!perms?.can_delete && !ro;
  const canPurge = !!perms?.can_permanent_delete;

  const exportNow = async () => {
    setExporting(true);
    try {
      const q = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (k !== "page" && k !== "limit" && v !== undefined && v !== "") q.set(k, String(v)); });
      if (tab === "deleted") q.set("deleted", "true");
      await downloadBlob(`/t/${code}/citizens/export?${q.toString()}`, `${code}_${tab === "deleted" ? "deleted_citizens" : "citizens"}.xlsx`);
      toast.success(`Export ready — ${data?.total ?? ""} citizen${data?.total === 1 ? "" : "s"} with linked property, demand and payment data (3 sheets)`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/t/${code}/users/${c.id}/toggle`);
      toast.success(c.is_active ? `${c.name} deactivated — they can no longer sign in` : `${c.name} activated`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/citizens/${deleteTarget.id}`, { params: deleteReason.trim() ? { reason: deleteReason.trim() } : {} });
      toast.success(`${deleteTarget.name} moved to Deleted Citizens — restorable until ${fmtDay(r.data.restore_until)}`);
      setDeleteTarget(null); setDeleteReason("");
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/citizens/${restoreTarget.id}/restore`);
      toast.success(`${restoreTarget.name} restored${r.data.linked_properties ? ` — ${r.data.linked_properties} linked propert${r.data.linked_properties === 1 ? "y" : "ies"} back in place` : ""}`);
      setRestoreTarget(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmPurge = async () => {
    if (!purgeTarget || purgeText.trim().toUpperCase() !== "DELETE") return;
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/citizens/${purgeTarget.id}/permanent`);
      toast.success(`${purgeTarget.name} permanently deleted — ${r.data.unlinked_properties} property link${r.data.unlinked_properties === 1 ? "" : "s"} cleared`);
      setPurgeTarget(null); setPurgeText("");
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const total = data?.total ?? 0;
  const totalAll = data?.total_all ?? 0;
  const isDeletedTab = tab === "deleted";

  // ---- Phase 216: selection (current page only — cleared whenever the page / tab / filters change)
  useEffect(() => { setSelected(new Set()); }, [tab, params]);
  const pageIds = useMemo(() => items.map((c) => c.id), [items]);
  const selectedOnPage = useMemo(() => items.filter((c) => selected.has(c.id)), [items, selected]);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPage = selectedOnPage.length > 0 && !allOnPage;
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAllOnPage = () => setSelected((s) => {
    if (allOnPage) { const n = new Set(s); pageIds.forEach((id) => n.delete(id)); return n; }
    const n = new Set(s); pageIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const selInactive = selectedOnPage.filter((c) => c.is_active === false);
  const selActive = selectedOnPage.filter((c) => c.is_active !== false);
  const selRestorable = selectedOnPage.filter((c) => c.restore_window_open || canPurge);
  const bulkTargets = (action) => {
    if (action === "activate") return selInactive;
    if (action === "deactivate") return selActive;
    if (action === "restore") return selRestorable;
    return selectedOnPage;
  };
  const BULK_META = {
    activate: { title: "Activate selected citizens", verb: "Activate", icon: UserCheck, past: "activated" },
    deactivate: { title: "Deactivate selected citizens", verb: "Deactivate", icon: UserX, past: "deactivated" },
    delete: { title: "Delete selected citizens", verb: "Delete", icon: Trash2, past: "deleted", destructive: true },
    restore: { title: "Restore selected citizens", verb: "Restore", icon: RotateCcw, past: "restored" },
  };
  const runBulk = async () => {
    const action = bulkAction;
    const targets = bulkTargets(action);
    if (!action || targets.length === 0) return;
    setBulkBusy(true);
    try {
      const body = { action, ids: targets.map((c) => c.id) };
      if (action === "delete" && bulkReason.trim()) body.reason = bulkReason.trim();
      const r = await api.post(`/t/${code}/citizens/bulk`, body);
      const d = r.data;
      const meta = BULK_META[action];
      if (d.failed > 0) {
        toast.warning(`${d.changed} citizen${d.changed === 1 ? "" : "s"} ${meta.past}, ${d.failed} failed`, { description: "See the details list for the rows that could not be processed." });
        setBulkResult(d);
      } else {
        toast.success(`${d.changed} citizen${d.changed === 1 ? "" : "s"} ${meta.past}${d.unchanged ? ` (${d.unchanged} already ${meta.past})` : ""}`);
      }
      setBulkAction(null); setBulkReason("");
      clearSelection();
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBulkBusy(false); }
  };
  const exportSelected = async () => {
    if (selectedOnPage.length === 0) return;
    setExporting(true);
    try {
      await downloadBlob(`/t/${code}/citizens/export`, `${code}_citizens_selected.xlsx`, { data: { ids: selectedOnPage.map((c) => c.id) } });
      toast.success(`Export ready — ${selectedOnPage.length} selected citizen${selectedOnPage.length === 1 ? "" : "s"} (3 sheets)`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const colCount = (isDeletedTab ? 7 : 9) + 1;

  if (access.loaded && !featureOk) {
    return (<div><PageHeader title="Citizens" /><NoAccessPanel what="Citizens" testId="citizens-no-access" /></div>);
  }

  return (
    <div data-testid="citizens-page">
      <PageHeader title="Citizens" subtitle="Citizen accounts registered with your municipality — linked properties, filters by Zone → Ward → Mohalla, Excel export, delete and restore">
        {canView && (
          <Button variant="outline" onClick={exportNow} disabled={exporting || loading || total === 0} data-testid="citizens-export-button"
            title="Download the citizens currently filtered on screen as an Excel workbook (Citizens / Demands / Payments)">
            <Download className="h-4 w-4 mr-2" /> {exporting ? "Preparing…" : `Export${total ? ` (${total.toLocaleString("en-IN")})` : ""}`}
          </Button>
        )}
      </PageHeader>

      {perms && !perms.can_view ? (
        <div className="rounded-lg border bg-muted/30 p-8 text-center" data-testid="citizens-view-off">
          <Lock className="h-7 w-7 mx-auto text-muted-foreground/70" />
          <p className="text-sm font-medium mt-2">Citizen accounts are not visible for this municipality</p>
          <p className="text-xs text-muted-foreground mt-1">The Super Admin has switched off citizen viewing for your municipality. Contact the platform team if you need access.</p>
        </div>
      ) : (
        <>
          {ro && <ReadOnlyNotice what="citizen accounts" testId="citizens-readonly-notice" className="mb-3" />}
          {perms && !perms.is_super_admin && !ro && (!perms.can_edit || !perms.can_delete) && (
            <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground mb-3" role="status" data-testid="citizens-permission-note">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                {!perms.can_edit && !perms.can_delete
                  ? "Editing and deleting citizen accounts are switched off for your municipality by the Super Admin — this page is view-only."
                  : !perms.can_edit
                    ? "Editing citizen accounts is switched off for your municipality by the Super Admin."
                    : "Deleting / restoring citizen accounts is switched off for your municipality by the Super Admin — only the Super Admin can delete or restore citizens."}
              </span>
            </div>
          )}

          <Tabs value={tab} onValueChange={changeTab} className="mb-4">
            <TabsList data-testid="citizens-tabs">
              <TabsTrigger value="active" data-testid="citizens-tab-active">
                <UsersIcon className="h-4 w-4 mr-1.5" /> Citizens
                {counts.live != null && <Badge variant="secondary" className="ml-1.5 text-[10px] tabular-nums" data-testid="citizens-tab-active-count">{counts.live}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="deleted" data-testid="citizens-tab-deleted">
                <UserX className="h-4 w-4 mr-1.5" /> Deleted Citizens
                {counts.deleted != null && <Badge variant="secondary" className="ml-1.5 text-[10px] tabular-nums" data-testid="citizens-tab-deleted-count">{counts.deleted}</Badge>}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={tab} className="mt-4">
              {/* totals strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="citizens-totals">
                <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] flex items-center justify-center"><UserCheck className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">Active citizens</p>
                    <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="citizens-total-active">{counts.active ?? "—"}</p>
                  </div>
                </CardContent></Card>
                <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-700 flex items-center justify-center"><EyeOff className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">Inactive</p>
                    <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="citizens-total-inactive">{counts.inactive ?? "—"}</p>
                  </div>
                </CardContent></Card>
                <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center"><Trash2 className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">Deleted</p>
                    <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="citizens-total-deleted">{counts.deleted ?? "—"}</p>
                  </div>
                </CardContent></Card>
                <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center"><UsersIcon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">Showing</p>
                    <p className="text-sm font-medium leading-tight" data-testid="citizens-showing">
                      {loading && !data ? "…" : <>{total.toLocaleString("en-IN")} of {totalAll.toLocaleString("en-IN")} {isDeletedTab ? "deleted " : ""}citizens</>}
                    </p>
                  </div>
                </CardContent></Card>
              </div>

              {/* filter bar */}
              <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="citizens-filter-bar">
                <Input placeholder="Search PIN, name, email or mobile…" value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="h-9 w-full sm:w-64" data-testid="citizens-search-input" />
                <Select value={zoneId || "all"} onValueChange={changeZone}>
                  <SelectTrigger className="h-9 w-40" data-testid="citizens-zone-filter"><SelectValue placeholder="Zone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All zones</SelectItem>
                    {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.zone_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={wardId || "all"} onValueChange={changeWard}>
                  <SelectTrigger className="h-9 w-40" data-testid="citizens-ward-filter"><SelectValue placeholder="Ward" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{zoneId ? "All wards in zone" : "All wards"}</SelectItem>
                    {wardsForZone.map((w) => <SelectItem key={w.id} value={w.id}>{w.ward_name}{!zoneId && zoneName[w.zone_id] ? ` · ${zoneName[w.zone_id]}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={mohallaId || "all"} onValueChange={changeMohalla}>
                  <SelectTrigger className="h-9 w-44" data-testid="citizens-mohalla-filter"><SelectValue placeholder="Mohalla" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{wardId ? "All mohallas in ward" : "All mohallas"}</SelectItem>
                    {mohallasForWard.slice(0, 500).map((m) => <SelectItem key={m.id} value={m.id}>{m.mohalla_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={status} onValueChange={changeStatus}>
                  <SelectTrigger className="h-9 w-36" data-testid="citizens-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="h-9 w-48" data-testid="citizens-sort-select">
                    <span className="flex items-center gap-1.5 truncate"><ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" /> <SelectValue /></span>
                  </SelectTrigger>
                  <SelectContent>
                    {(isDeletedTab ? SORTS_DELETED : SORTS_ACTIVE).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {filtersActive && (
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={resetFilters} data-testid="citizens-clear-filters">
                    <X className="h-3.5 w-3.5 mr-1" /> Clear filters
                  </Button>
                )}
                {!isDeletedTab && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <Switch checked={hideInactive} onCheckedChange={setHide} data-testid="citizens-hide-inactive-toggle" />
                    <span className="flex items-center gap-1.5">
                      {hideInactive ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      Hide inactive
                    </span>
                  </label>
                )}
                {!isDeletedTab && hideInactive && (counts.inactive || 0) > 0 && (
                  <Badge variant="secondary" className="font-normal" data-testid="citizens-hidden-inactive-badge">
                    {counts.inactive} inactive hidden
                  </Badge>
                )}
              </div>

              {/* Phase 216: bulk action bar (current-page selection) */}
              {selectedOnPage.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 mb-3" role="region" aria-label="Bulk actions" data-testid="citizens-bulk-bar">
                  <span className="flex items-center gap-1.5 text-sm font-medium mr-1" data-testid="citizens-bulk-count">
                    <CheckSquare className="h-4 w-4 text-primary" /> {selectedOnPage.length} selected
                  </span>
                  {!isDeletedTab && canEdit && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={selInactive.length === 0} onClick={() => setBulkAction("activate")}
                      title={selInactive.length === 0 ? "No inactive citizens in the selection" : `Activate ${selInactive.length} inactive citizen(s)`} data-testid="citizens-bulk-activate">
                      <UserCheck className="h-3.5 w-3.5 mr-1" /> Activate{selInactive.length ? ` (${selInactive.length})` : ""}
                    </Button>
                  )}
                  {!isDeletedTab && canEdit && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={selActive.length === 0} onClick={() => setBulkAction("deactivate")}
                      title={selActive.length === 0 ? "No active citizens in the selection" : `Deactivate ${selActive.length} active citizen(s)`} data-testid="citizens-bulk-deactivate">
                      <UserX className="h-3.5 w-3.5 mr-1" /> Deactivate{selActive.length ? ` (${selActive.length})` : ""}
                    </Button>
                  )}
                  {!isDeletedTab && canDelete && (
                    <Button size="sm" variant="outline" className="h-8 text-xs text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive" onClick={() => { setBulkAction("delete"); setBulkReason(""); }}
                      data-testid="citizens-bulk-delete">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete ({selectedOnPage.length})
                    </Button>
                  )}
                  {isDeletedTab && canDelete && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={selRestorable.length === 0} onClick={() => setBulkAction("restore")}
                      title={selRestorable.length === 0 ? "The restore window has closed for the selected citizens — Super Admin only" : `Restore ${selRestorable.length} citizen(s)`} data-testid="citizens-bulk-restore">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore{selRestorable.length ? ` (${selRestorable.length})` : ""}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={exporting} onClick={exportSelected} data-testid="citizens-bulk-export">
                    <Download className="h-3.5 w-3.5 mr-1" /> {exporting ? "Preparing…" : `Export selected (${selectedOnPage.length})`}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs ml-auto" onClick={clearSelection} data-testid="citizens-bulk-clear">
                    <X className="h-3.5 w-3.5 mr-1" /> Clear selection
                  </Button>
                </div>
              )}

              {error ? (
                <Card className="shadow-sm"><CardContent className="p-8 text-center" data-testid="citizens-error">
                  <p className="text-sm font-medium">Could not load citizens</p>
                  <p className="text-xs text-muted-foreground mt-1">{error}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={refresh} data-testid="citizens-retry">Retry</Button>
                </CardContent></Card>
              ) : (
                <Card className="shadow-sm">
                  <CardContent className="p-0">
                    {loading && !data ? <TableRowsSkeleton rows={6} cols={6} testId="citizens-skeleton" /> : (
                      <Table data-testid={isDeletedTab ? "deleted-citizens-table" : "citizens-table"}>
                        <TableHeader>
                          {isDeletedTab ? (
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox checked={allOnPage ? true : someOnPage ? "indeterminate" : false} onCheckedChange={toggleAllOnPage} disabled={items.length === 0}
                                  aria-label="Select all deleted citizens on this page" className="data-[state=indeterminate]:bg-primary/60 data-[state=indeterminate]:text-primary-foreground" data-testid="citizens-select-all" />
                              </TableHead>
                              <TableHead>Citizen</TableHead>
                              <TableHead className="hidden md:table-cell">Mobile</TableHead>
                              <TableHead>Property PIN</TableHead>
                              <TableHead>Deleted on</TableHead>
                              <TableHead className="hidden lg:table-cell">Deleted by</TableHead>
                              <TableHead>Last date to restore</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          ) : (
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox checked={allOnPage ? true : someOnPage ? "indeterminate" : false} onCheckedChange={toggleAllOnPage} disabled={items.length === 0}
                                  aria-label="Select all citizens on this page" className="data-[state=indeterminate]:bg-primary/60 data-[state=indeterminate]:text-primary-foreground" data-testid="citizens-select-all" />
                              </TableHead>
                              <TableHead>Property PIN</TableHead>
                              <TableHead>Citizen</TableHead>
                              <TableHead className="hidden md:table-cell">Mobile</TableHead>
                              <TableHead className="hidden lg:table-cell">Zone</TableHead>
                              <TableHead className="hidden lg:table-cell">Ward</TableHead>
                              <TableHead className="hidden xl:table-cell">Mohalla</TableHead>
                              <TableHead className="text-right hidden md:table-cell">Balance due</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          )}
                        </TableHeader>
                        <TableBody>
                          {items.length === 0 && (
                            <TableRow data-testid="citizens-empty-row">
                              <TableCell colSpan={colCount} className="py-6">
                                <EmptyState
                                  title={isDeletedTab ? "No deleted citizens" : filtersActive ? "No citizens match these filters" : "No citizen accounts yet"}
                                  subtitle={isDeletedTab
                                    ? "Citizens you delete appear here and can be restored until the end of the financial year in which they were deleted."
                                    : filtersActive ? "Try a different zone / ward / mohalla, status or search term." : "Citizens register from the citizen portal and link their properties by PIN."}
                                  action={filtersActive ? <Button variant="outline" size="sm" onClick={resetFilters} data-testid="citizens-empty-clear">Clear filters</Button> : null} />
                              </TableCell>
                            </TableRow>
                          )}
                          {items.map((c) => (
                            isDeletedTab ? (
                              <TableRow key={c.id} className={`hover:bg-muted/60 ${selected.has(c.id) ? "bg-primary/5" : ""}`} data-testid={`deleted-citizen-row-${c.email}`} data-selected={selected.has(c.id) ? "true" : "false"}>
                                <TableCell className="w-10">
                                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} aria-label={`Select ${c.name}`} data-testid={`citizen-select-${c.email}`} />
                                </TableCell>
                                <TableCell>
                                  <span className="flex items-center gap-2">
                                    <CitizenAvatar name={c.name} deleted />
                                    <span className="min-w-0">
                                      <span className="block font-medium text-sm">{c.name}</span>
                                      <span className="block text-[11px] text-muted-foreground truncate">{c.email}</span>
                                    </span>
                                  </span>
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-sm">{c.mobile || "-"}</TableCell>
                                <TableCell><PinCell citizen={c} testId={`deleted-citizen-pins-${c.email}`} /></TableCell>
                                <TableCell className="text-sm tabular-nums" data-testid={`deleted-citizen-on-${c.email}`}>{fmtDateIST(c.deleted_at)}</TableCell>
                                <TableCell className="hidden lg:table-cell text-sm" data-testid={`deleted-citizen-by-${c.email}`}>
                                  {c.deleted_by_name || "-"}
                                  {c.deleted_by_role && <span className="block text-[10px] text-muted-foreground capitalize">{String(c.deleted_by_role).replace(/_/g, " ")}</span>}
                                </TableCell>
                                <TableCell><RestoreWindowBadge citizen={c} testId={`deleted-citizen-until-${c.email}`} /></TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                  {canDelete && (c.restore_window_open || canPurge) ? (
                                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setRestoreTarget(c)} data-testid={`citizen-restore-${c.email}`}>
                                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                                    </Button>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`citizen-restore-locked-${c.email}`}
                                      title={!c.restore_window_open ? "The restore window has closed — only the Super Admin can restore or permanently delete" : "Deleting / restoring citizens is switched off for your municipality"}>
                                      <Lock className="h-3.5 w-3.5" /> Super Admin only
                                    </span>
                                  )}
                                  {canPurge && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8 ml-1 text-destructive hover:text-destructive" title="Permanently delete (Super Admin)"
                                      onClick={() => { setPurgeTarget(c); setPurgeText(""); }} data-testid={`citizen-purge-${c.email}`}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : (
                              <TableRow key={c.id} className={`hover:bg-muted/60 ${selected.has(c.id) ? "bg-primary/5" : ""}`} data-testid={`citizen-row-${c.email}`} data-selected={selected.has(c.id) ? "true" : "false"}>
                                <TableCell className="w-10">
                                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} aria-label={`Select ${c.name}`} data-testid={`citizen-select-${c.email}`} />
                                </TableCell>
                                <TableCell><PinCell citizen={c} testId={`citizen-pins-${c.email}`} /></TableCell>
                                <TableCell>
                                  <span className="flex items-center gap-2">
                                    <CitizenAvatar name={c.name} />
                                    <span className="min-w-0">
                                      <span className="block font-medium text-sm" data-testid={`citizen-name-${c.email}`}>{c.name}</span>
                                      <span className="block text-[11px] text-muted-foreground truncate">{c.email}</span>
                                      <span className="block md:hidden text-[11px] text-muted-foreground">{c.mobile}</span>
                                    </span>
                                  </span>
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-sm">{c.mobile || "-"}</TableCell>
                                <TableCell className="hidden lg:table-cell text-sm" data-testid={`citizen-zone-${c.email}`}>{joinLabels(c.zone_labels) || <span className="text-muted-foreground">—</span>}</TableCell>
                                <TableCell className="hidden lg:table-cell text-sm" data-testid={`citizen-ward-${c.email}`}>{joinLabels(c.ward_labels) || <span className="text-muted-foreground">—</span>}</TableCell>
                                <TableCell className="hidden xl:table-cell text-sm" data-testid={`citizen-mohalla-${c.email}`}>{joinLabels(c.mohalla_labels) || <span className="text-muted-foreground">—</span>}</TableCell>
                                <TableCell className="text-right hidden md:table-cell text-sm tabular-nums" data-testid={`citizen-balance-${c.email}`}>
                                  {c.property_count ? (
                                    <span className={c.balance_due > 0 ? "text-amber-700 font-medium" : "text-[hsl(var(--success))]"}>{fmtINR(c.balance_due)}</span>
                                  ) : <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell>
                                  <span className="flex items-center gap-2">
                                    <StatusBadge status={c.is_active ? "active" : "inactive"} />
                                    {canEdit && <Switch checked={!!c.is_active} onCheckedChange={() => toggleActive(c)} data-testid={`citizen-toggle-${c.email}`} aria-label={`${c.is_active ? "Deactivate" : "Activate"} ${c.name}`} />}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right whitespace-nowrap">
                                  {canEdit && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit citizen details" onClick={() => setEditTarget(c)} data-testid={`citizen-edit-${c.email}`}>
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canEdit && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Menu access (citizen portal)" onClick={() => setAccessTarget(c)} data-testid={`citizen-access-${c.email}`}>
                                      <ShieldCheck className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canEdit && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Reset password" onClick={() => setResetTarget(c)} data-testid={`citizen-reset-password-${c.email}`}>
                                      <KeyRound className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDelete && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete citizen (moves to Deleted Citizens)"
                                      onClick={() => { setDeleteTarget(c); setDeleteReason(""); }} data-testid={`citizen-delete-${c.email}`}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {!canEdit && !canDelete && <span className="text-[11px] text-muted-foreground" data-testid={`citizen-no-actions-${c.email}`}>View only</span>}
                                </TableCell>
                              </TableRow>
                            )
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              )}
              {data && total > 0 && (
                <PaginationBar page={data.page || page} limit={limit} total={total} loading={loading}
                  onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }} testId="citizens-pagination" />
              )}
              <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5" data-testid="citizens-footnote">
                <Home className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                {isDeletedTab
                  ? "A deleted citizen cannot sign in; their property link is kept (dormant) so a restore brings everything back exactly. Property records, demands and receipts are never affected. After the restore window only the Super Admin can restore or permanently delete the account."
                  : "Zone, Ward and Mohalla come from the citizen's linked properties. Tick rows (or the header box for the whole page) to activate, deactivate, delete or export several citizens at once. Export includes complete citizen data plus linked property, demand and payment details in three sheets."}
              </p>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* dialogs */}
      <EditUserDialog code={code} user={editTarget} customFields={[]} onOpenChange={(v) => !v && setEditTarget(null)} onSaved={refresh} />
      <ResetPasswordDialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}
        targetLabel={resetTarget ? `${resetTarget.name} (${resetTarget.email})` : ""}
        onSubmit={async (pw) => {
          const r = await api.post(`/t/${code}/users/${resetTarget.id}/reset-password`, { new_password: pw });
          toast.success(r.data.message || "Password reset");
        }} />
      <UserAccessDialog code={code} user={accessTarget} open={!!accessTarget} onOpenChange={(v) => !v && setAccessTarget(null)} onSaved={refresh} />

      {/* soft delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteReason(""); } }}>
        <AlertDialogContent data-testid="citizen-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Trash2 className="h-4 w-4 text-destructive" /> Delete citizen "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>The account moves to <b>Deleted Citizens</b>. The citizen can no longer sign in to the citizen portal.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>{deleteTarget?.property_count ? `${deleteTarget.property_count} linked propert${deleteTarget.property_count === 1 ? "y is" : "ies are"} kept (dormant) — ` : "Any linked property is kept (dormant) — "}property records, demands and receipts are never changed.</li>
                  <li>Your municipality can restore the account until the <b>end of the current financial year (31 March)</b>. After that only the Super Admin can restore or permanently delete it.</li>
                  <li>This action is recorded in the Audit Trail with your name and the date / time.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label htmlFor="citizen-delete-reason" className="text-xs">Reason (optional)</Label>
            <Textarea id="citizen-delete-reason" rows={2} value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g. duplicate account, citizen requested removal" data-testid="citizen-delete-reason" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="citizen-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="citizen-delete-confirm">
              {busy ? "Deleting…" : "Delete citizen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* restore */}
      <AlertDialog open={!!restoreTarget} onOpenChange={(v) => !v && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="citizen-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Restore "{restoreTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The citizen account comes back {restoreTarget?.was_active_before_delete === false ? "as inactive (it was inactive before deletion)" : "active"} and can sign in again.
              {restoreTarget?.property_count ? ` ${restoreTarget.property_count} linked propert${restoreTarget.property_count === 1 ? "y" : "ies"} (${(restoreTarget.pins || []).slice(0, 3).join(", ")}${(restoreTarget.pins || []).length > 3 ? ", …" : ""}) will show in their portal exactly as before.` : ""}
              {" "}Recorded in the Audit Trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="citizen-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRestore(); }} disabled={busy} data-testid="citizen-restore-confirm">
              {busy ? "Restoring…" : "Restore citizen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* permanent delete (Super Admin) */}
      <AlertDialog open={!!purgeTarget} onOpenChange={(v) => { if (!v) { setPurgeTarget(null); setPurgeText(""); } }}>
        <AlertDialogContent data-testid="citizen-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Permanently delete "{purgeTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>This removes the citizen's login account for good and clears the link on {purgeTarget?.property_count || 0} propert{purgeTarget?.property_count === 1 ? "y" : "ies"}. Property records, demands and receipts stay untouched. <b>This cannot be undone.</b></p>
                <p className="text-xs">Type <span className="font-mono font-semibold">DELETE</span> to confirm.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="DELETE" autoComplete="off" data-testid="citizen-purge-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="citizen-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmPurge(); }} disabled={busy || purgeText.trim().toUpperCase() !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="citizen-purge-confirm">
              {busy ? "Deleting…" : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 216: bulk confirm */}
      <AlertDialog open={!!bulkAction} onOpenChange={(v) => { if (!v) { setBulkAction(null); setBulkReason(""); } }}>
        <AlertDialogContent data-testid="citizens-bulk-dialog">
          {bulkAction && (() => {
            const meta = BULK_META[bulkAction];
            const targets = bulkTargets(bulkAction);
            const skipped = selectedOnPage.length - targets.length;
            const Icon = meta.icon;
            const withProps = targets.reduce((n, c) => n + (c.property_count || 0), 0);
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle className={`font-heading flex items-center gap-2 ${meta.destructive ? "text-destructive" : ""}`}>
                    <Icon className="h-4 w-4" /> {meta.verb} {targets.length} citizen{targets.length === 1 ? "" : "s"}?
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {bulkAction === "activate" && <p>The selected inactive accounts will be able to sign in to the citizen portal again.</p>}
                      {bulkAction === "deactivate" && <p>The selected accounts will no longer be able to sign in. Their property links stay in place; nothing else changes.</p>}
                      {bulkAction === "delete" && (
                        <>
                          <p>The selected accounts move to <b>Deleted Citizens</b> and can no longer sign in.</p>
                          <ul className="list-disc pl-5 space-y-1 text-xs">
                            <li>{withProps} linked propert{withProps === 1 ? "y is" : "ies are"} kept (dormant) — property records, demands and receipts are never changed.</li>
                            <li>Your municipality can restore them until the <b>end of the current financial year (31 March)</b>; after that only the Super Admin can restore or permanently delete.</li>
                          </ul>
                        </>
                      )}
                      {bulkAction === "restore" && <p>The selected accounts come back exactly as they were (same active state, property links intact) and can sign in again.</p>}
                      {skipped > 0 && (
                        <p className="text-xs rounded-md bg-muted px-2 py-1.5" data-testid="citizens-bulk-skipped">
                          {skipped} of the {selectedOnPage.length} selected will be skipped —
                          {bulkAction === "activate" ? " already active" : bulkAction === "deactivate" ? " already inactive" : " their restore window has closed (Super Admin only)"}.
                        </p>
                      )}
                      <p className="text-xs">Each account is recorded individually in the Audit Trail, plus one summary entry for this bulk action.</p>
                      <ul className="text-xs max-h-28 overflow-y-auto rounded-md border px-2 py-1.5 space-y-0.5" data-testid="citizens-bulk-targets">
                        {targets.slice(0, 50).map((c) => <li key={c.id} className="truncate">{c.name} <span className="text-muted-foreground">· {c.email}</span></li>)}
                        {targets.length > 50 && <li className="text-muted-foreground">… and {targets.length - 50} more</li>}
                      </ul>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {bulkAction === "delete" && (
                  <div className="space-y-1">
                    <Label htmlFor="citizens-bulk-reason" className="text-xs">Reason (optional, applied to each citizen)</Label>
                    <Textarea id="citizens-bulk-reason" rows={2} value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
                      placeholder="e.g. duplicate accounts from the import" data-testid="citizens-bulk-reason" />
                  </div>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="citizens-bulk-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={(e) => { e.preventDefault(); runBulk(); }} disabled={bulkBusy || targets.length === 0}
                    className={meta.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid="citizens-bulk-confirm">
                    {bulkBusy ? "Working…" : `${meta.verb} ${targets.length}`}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 216: partial-failure details */}
      <AlertDialog open={!!bulkResult} onOpenChange={(v) => !v && setBulkResult(null)}>
        <AlertDialogContent data-testid="citizens-bulk-result-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Info className="h-4 w-4" /> Bulk action finished with some failures</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p data-testid="citizens-bulk-result-summary">{bulkResult?.summary}</p>
                <ul className="text-xs max-h-48 overflow-y-auto rounded-md border divide-y" data-testid="citizens-bulk-failures">
                  {(bulkResult?.results || []).filter((r) => !r.ok).map((r) => (
                    <li key={r.id} className="px-2 py-1.5">
                      <span className="font-medium text-foreground">{r.name || r.email || r.id}</span>
                      <span className="block text-destructive">{r.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="citizens-bulk-result-close">Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
